import { useState, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShareableWorkout } from './ShareWorkoutCard';
import {
  ShareTemplateClassic,
  ShareTemplateMinimal,
  ShareTemplateBold,
  ShareTemplateRoute,
  ShareTemplateSplits,
  ShareTemplateGymExercises,
  ShareTemplateGymSimple,
  LiveRouteCapture,
} from './share';
import { Loader2, ChevronLeft, ChevronRight, Camera as CameraIcon, Image as ImageIcon, X } from 'lucide-react';
import { ShareIcon } from './icons/ShareIcon';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { Capacitor } from '@capacitor/core';
import { captureWebViewAsDataUrl, isNativeScreenshotAvailable } from '@/lib/nativeScreenshot';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Media } from '@capacitor-community/media';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { usePremium } from '@/hooks/usePremium';
import { isShareTemplatePremiumOnly } from '@/lib/premiumFeatures';

type TemplateType =
  | 'classic'
  | 'minimal'
  | 'bold'
  | 'route'
  | 'splits'
  | 'splitsTransparent'
  | 'gymExercises'
  | 'gymSimple';

const TEMPLATE_NAME_KEYS: Record<TemplateType, string> = {
  classic: 'components.shareWorkoutExtra.templates.classic',
  minimal: 'components.shareWorkoutExtra.templates.minimal',
  bold: 'components.shareWorkoutExtra.templates.bold',
  route: 'components.shareWorkoutExtra.templates.route',
  splits: 'components.shareWorkoutExtra.templates.splits',
  splitsTransparent: 'components.shareWorkoutExtra.templates.splitsTransparent',
  gymExercises: 'components.shareWorkoutExtra.templates.gymExercises',
  gymSimple: 'components.shareWorkoutExtra.templates.gymSimple',
};

// Templates that have a transparent canvas - these are eligible for "use as overlay on photo"
const TRANSPARENT_TEMPLATES: ReadonlySet<TemplateType> = new Set<TemplateType>([
  'minimal',
  'splitsTransparent',
  'gymExercises',
  'gymSimple',
]);

// Templates only relevant to running workouts
const RUN_ONLY_TEMPLATES: ReadonlySet<TemplateType> = new Set<TemplateType>([
  'route',
  'splits',
  'splitsTransparent',
]);

// Templates only relevant to gym/strength workouts (require exercise data)
const GYM_ONLY_TEMPLATES: ReadonlySet<TemplateType> = new Set<TemplateType>([
  'gymExercises',
  'gymSimple',
]);

interface ShareWorkoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  workout: ShareableWorkout | null;
}

export const ShareWorkoutModal = ({ isOpen, onClose, workout }: ShareWorkoutModalProps) => {
  const { t } = useTranslation();
  const { isPremium } = usePremium();
  const cardRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Wraps the in-modal `LiveRouteCapture` while we're taking the native
  // screenshot. We pass this element's bounding rect to the plugin so WebKit
  // only snapshots the preview slot - the rest of the modal stays exactly as
  // the user sees it (no full-screen takeover).
  const liveRouteHostRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [overlayImage, setOverlayImage] = useState<string | null>(null);
  const [backgroundPhoto, setBackgroundPhoto] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [isSavingToPhotos, setIsSavingToPhotos] = useState(false);
  const [saveSuccessToast, setSaveSuccessToast] = useState(false);
  const [isPickingPhoto, setIsPickingPhoto] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>('classic');
  // True while the LiveRouteCapture is mounted in the preview slot and a
  // native WebKit snapshot is in flight. Mounting it eagerly while the modal
  // is merely open would force MapKit JS to initialise a second map instance
  // that the user would never get to see.
  const [isCapturingLiveRoute, setIsCapturingLiveRoute] = useState(false);

  const isRunningWorkout = workout?.workout_type?.toLowerCase() === 'running';
  const hasRouteCoords = (workout?.route_coordinates?.length ?? 0) >= 2;
  const hasSplits = (workout?.splits?.length ?? 0) > 0;
  const hasExercises = (workout?.exercises?.length ?? 0) > 0;

  // Restrict the template carousel to those that make sense for this workout.
  const templates = useMemo<TemplateType[]>(() => {
    const all: TemplateType[] = [
      'classic',
      'minimal',
      'bold',
      'route',
      'splits',
      'splitsTransparent',
      'gymExercises',
      'gymSimple',
    ];
    return all.filter(t => {
      if (!isPremium && isShareTemplatePremiumOnly(t)) return false;
      if (RUN_ONLY_TEMPLATES.has(t) && !isRunningWorkout) return false;
      if (GYM_ONLY_TEMPLATES.has(t) && (isRunningWorkout || !hasExercises)) return false;
      if (t === 'route' && !hasRouteCoords) return false;
      if ((t === 'splits' || t === 'splitsTransparent') && !hasSplits) return false;
      return true;
    });
  }, [isRunningWorkout, hasRouteCoords, hasSplits, hasExercises, isPremium]);

  // If the previously-selected template gets filtered out, fall back to first available.
  useEffect(() => {
    if (!templates.includes(selectedTemplate)) {
      setSelectedTemplate(templates[0] ?? 'classic');
    }
  }, [templates, selectedTemplate]);

  const isTransparentTemplate = TRANSPARENT_TEMPLATES.has(selectedTemplate);
  const allowPhotoOverlay = isPremium && isTransparentTemplate;

  // Generate image when modal opens, template changes, or background photo changes.
  // Render template in portal to body for consistent capture (avoids modal layout affecting html2canvas)
  useEffect(() => {
    if (isOpen && workout) {
      setGeneratedImage(null);
      setOverlayImage(null);
      const timer = setTimeout(() => {
        if (cardRef.current) {
          generateImage();
        }
      }, 350);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setGeneratedImage(null);
      setOverlayImage(null);
      setBackgroundPhoto(null);
      setSelectedTemplate('classic');
      setSaveSuccessToast(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, workout, selectedTemplate, backgroundPhoto]);

  /**
   * Native-screenshot path for the Route template: render the LiveRouteCapture
   * (a self-contained view with the live MapKit JS map + data overlays) inside
   * the modal's preview slot, wait for MapKit to actually paint tiles, then
   * ask WebKit to snapshot ONLY that slot's rect. The rest of the modal stays
   * exactly as the user sees it - no full-screen takeover, no flash of a
   * "non-shareable" view.
   *
   * We have to use the native snapshot (not html2canvas) because MapKit
   * renders into a cross-origin-tainted WebGL canvas html2canvas can't read.
   */
  const captureLiveRoute = async (): Promise<string | null> => {
    if (!isNativeScreenshotAvailable()) return null;

    setIsCapturingLiveRoute(true);
    try {
      // Give React a tick to actually mount the overlay, MapKit time to
      // load tiles + perform fitToRoute, and the GPU one final frame to
      // commit. ~2.5s is what the run-summary Expand view also looks like
      // it needs; 3s adds slack for cold MapKit script load.
      await new Promise(resolve => setTimeout(resolve, 2800));

      // Translate the in-modal preview slot to WebView coordinates so the
      // native plugin only snapshots that rectangle. getBoundingClientRect()
      // returns CSS points which match the WKWebView coordinate system 1:1
      // (no zoom in our app), so we can pass the values straight through.
      let rect: { x: number; y: number; width: number; height: number } | undefined;
      const host = liveRouteHostRef.current;
      if (host) {
        const r = host.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          rect = {
            x: Math.max(0, Math.round(r.left)),
            y: Math.max(0, Math.round(r.top)),
            width: Math.round(r.width),
            height: Math.round(r.height),
          };
        }
      }

      const dataUrl = await captureWebViewAsDataUrl(rect ? { rect } : {});
      return dataUrl;
    } catch (err) {
      console.warn('[ShareWorkoutModal] live route capture failed:', err);
      return null;
    } finally {
      setIsCapturingLiveRoute(false);
    }
  };

  // Capture the template DOM into a PNG, and (if a background photo is set) composite it on top.
  const generateImage = async () => {
    if (!cardRef.current) return;

    setIsGenerating(true);
    try {
      // Wait for fonts and bundled images to load.
      await new Promise(resolve => setTimeout(resolve, 400));

      // Route template: try the native-screenshot path first so the share
      // image actually contains the Apple Maps basemap. Falls through to the
      // static html2canvas template if the plugin isn't available (web
      // preview, plugin missing, etc.).
      if (selectedTemplate === 'route' && isNativeScreenshotAvailable()) {
        const liveDataUrl = await captureLiveRoute();
        if (liveDataUrl) {
          setOverlayImage(liveDataUrl);
          setGeneratedImage(liveDataUrl);
          return;
        }
        // If we got here, the native capture failed; fall back to the static
        // route template via html2canvas below so the user still sees
        // *something* shareable.
      }

      // Always capture the template with a transparent canvas when we plan to
      // composite over a photo. For non-photo flows, transparent templates also
      // capture transparent so they download with proper alpha.
      const captureTransparent = isTransparentTemplate || backgroundPhoto != null;

      const overlayCanvas = await html2canvas(cardRef.current, {
        backgroundColor: captureTransparent ? null : '#0a0a0a',
        scale: 2,
        useCORS: true,
        logging: false,
        scrollX: 0,
        scrollY: 0,
      });

      const overlayDataUrl = overlayCanvas.toDataURL('image/png');
      setOverlayImage(overlayDataUrl);

      let finalDataUrl: string;
      if (backgroundPhoto && captureTransparent) {
        finalDataUrl = await compositeOnPhoto(backgroundPhoto, overlayDataUrl, overlayCanvas.width, overlayCanvas.height);
      } else {
        finalDataUrl = overlayDataUrl;
      }

      setGeneratedImage(finalDataUrl);
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error(t('components.shareWorkoutExtra.toast.generateFailed'));
    } finally {
      setIsGenerating(false);
    }
  };

  // Compose: load both images, fill background to overlay aspect (cover), then draw overlay on top.
  const compositeOnPhoto = (photoDataUrl: string, overlayDataUrl: string, w: number, h: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const photo = new Image();
      photo.crossOrigin = 'anonymous';
      photo.onload = () => {
        const overlay = new Image();
        overlay.crossOrigin = 'anonymous';
        overlay.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = w;
            canvas.height = h;
            const ctx = canvas.getContext('2d');
            if (!ctx) {
              reject(new Error('Could not get 2d context'));
              return;
            }
            // Cover-fit the photo onto the canvas
            const photoRatio = photo.width / photo.height;
            const canvasRatio = w / h;
            let drawW: number;
            let drawH: number;
            let dx: number;
            let dy: number;
            if (photoRatio > canvasRatio) {
              drawH = h;
              drawW = h * photoRatio;
              dx = (w - drawW) / 2;
              dy = 0;
            } else {
              drawW = w;
              drawH = w / photoRatio;
              dx = 0;
              dy = (h - drawH) / 2;
            }
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(photo, dx, dy, drawW, drawH);
            // Soft dark scrim so overlay text stays legible regardless of photo brightness
            const grad = ctx.createLinearGradient(0, 0, 0, h);
            grad.addColorStop(0, 'rgba(0,0,0,0.35)');
            grad.addColorStop(1, 'rgba(0,0,0,0.55)');
            ctx.fillStyle = grad;
            ctx.fillRect(0, 0, w, h);
            ctx.drawImage(overlay, 0, 0, w, h);
            resolve(canvas.toDataURL('image/png'));
          } catch (err) {
            reject(err);
          }
        };
        overlay.onerror = () => reject(new Error('Failed to load overlay'));
        overlay.src = overlayDataUrl;
      };
      photo.onerror = () => reject(new Error('Failed to load photo'));
      photo.src = photoDataUrl;
    });
  };

  const pickPhotoNative = async (source: 'camera' | 'library') => {
    setIsPickingPhoto(true);
    try {
      const photo = await Camera.getPhoto({
        quality: 90,
        resultType: CameraResultType.DataUrl,
        source: source === 'camera' ? CameraSource.Camera : CameraSource.Photos,
        allowEditing: false,
      });
      if (photo.dataUrl) {
        setBackgroundPhoto(photo.dataUrl);
      }
    } catch (e: any) {
      // User cancellation throws - swallow silently
      const msg = (e?.message || '').toLowerCase();
      if (!msg.includes('cancel') && !msg.includes('user denied')) {
        console.error('[Share] Photo pick failed:', e);
        toast.error(t('components.shareWorkoutExtra.toast.photoLoadFailed'));
      }
    } finally {
      setIsPickingPhoto(false);
    }
  };

  const pickPhotoWeb = (capture: boolean) => {
    if (!fileInputRef.current) return;
    fileInputRef.current.accept = 'image/*';
    if (capture) {
      fileInputRef.current.setAttribute('capture', 'environment');
    } else {
      fileInputRef.current.removeAttribute('capture');
    }
    fileInputRef.current.click();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const dataUrl = ev.target?.result;
      if (typeof dataUrl === 'string') {
        setBackgroundPhoto(dataUrl);
      }
    };
    reader.onerror = () => toast.error(t('components.shareWorkoutExtra.toast.photoReadFailed'));
    reader.readAsDataURL(file);
    // reset value so picking the same file twice still triggers onChange
    e.target.value = '';
  };

  const handlePickPhoto = (source: 'camera' | 'library') => {
    if (Capacitor.isNativePlatform()) {
      pickPhotoNative(source);
    } else {
      pickPhotoWeb(source === 'camera');
    }
  };

  const downloadFallback = () => {
    if (!generatedImage) return;
    if (Capacitor.isNativePlatform()) {
      toast.info(t('components.shareWorkoutExtra.toast.chooseSaveOption'));
      handleShare();
      return;
    }
    const link = document.createElement('a');
    link.download = `straiv-workout-${workout?.id || 'summary'}-${selectedTemplate}.png`;
    link.href = generatedImage;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(t('components.shareWorkoutExtra.toast.savedDownloads'));
  };

  const getAlbumIdentifierForSave = async (): Promise<string | undefined> => {
    if (Capacitor.getPlatform() !== 'android') return undefined;
    try {
      const { albums } = await Media.getAlbums();
      if (albums && albums.length > 0) {
        const camera = albums.find((a) => a.name === 'Camera' || a.name === 'DCIM');
        const pics = albums.find((a) => a.name === 'Pictures');
        const straiv = albums.find((a) => a.name === 'Straiv');
        const first = albums[0];
        return (camera || pics || straiv || first)?.identifier;
      }
      const { path } = await Media.getAlbumsPath();
      if (path) {
        try {
          await Media.createAlbum({ name: 'Straiv' });
        } catch {
          // Album may already exist
        }
        const { albums } = await Media.getAlbums();
        const straiv = albums?.find((a) => a.name === 'Straiv');
        if (straiv?.identifier) return straiv.identifier;
        return path ? `${path}/Straiv` : undefined;
      }
    } catch (e) {
      console.warn('[Share] Could not get album:', e);
    }
    return undefined;
  };

  const handleSaveToPhotos = async () => {
    if (!generatedImage) return;
    setIsSavingToPhotos(true);
    setSaveSuccessToast(false);
    toast.info(t('components.shareWorkoutExtra.toast.savingPhotos'), { duration: 1500, style: { zIndex: 2147483647 } });
    try {
      const albumIdentifier = await getAlbumIdentifierForSave();
      const opts: { path: string; albumIdentifier?: string; fileName?: string } = {
        path: generatedImage,
        fileName: `straiv-workout-${workout?.id || 'summary'}-${selectedTemplate}`.replace(/[^a-zA-Z0-9._-]/g, '_'),
      };
      if (albumIdentifier) opts.albumIdentifier = albumIdentifier;
      await Media.savePhoto(opts);
      setSaveSuccessToast(true);
      toast.success(t('components.shareWorkout.savedBanner'), { duration: 4000, style: { zIndex: 2147483647 } });
      setTimeout(() => setSaveSuccessToast(false), 4000);
    } catch (error) {
      console.error('Error saving to Photos:', error);
      toast.error(t('components.shareWorkoutExtra.toast.saveFailed'));
      downloadFallback();
    } finally {
      setIsSavingToPhotos(false);
    }
  };

  const handleShare = async () => {
    if (!generatedImage) {
      toast.error(t('components.shareWorkoutExtra.toast.waitImage'));
      return;
    }

    setIsSharing(true);
    try {
      const fileName = `straiv-workout-${workout?.id || 'summary'}-${selectedTemplate}.png`;
      const shareText = t('components.shareWorkoutExtra.shareText', { minutes: workout?.duration_minutes, title: workout?.title });

      if (Capacitor.isNativePlatform()) {
        if (navigator.share && navigator.canShare) {
          try {
            const response = await fetch(generatedImage);
            const blob = await response.blob();
            const file = new File([blob], fileName, { type: 'image/png' });
            if (navigator.canShare({ files: [file] })) {
              await navigator.share({
                title: t('components.shareWorkout.shareMetaTitle'),
                text: shareText,
                files: [file],
              });
              toast.success(t('components.shareWorkoutExtra.toast.shared'));
              return;
            }
          } catch (webShareErr: any) {
            if (webShareErr.name !== 'AbortError') {
              console.warn('[Share] Web Share API failed, trying native:', webShareErr);
            }
          }
        }

        const base64Data = generatedImage.replace(/^data:image\/png;base64,/, '');
        const path = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
        await Filesystem.writeFile({
          path,
          data: base64Data,
          directory: Directory.Cache,
        });
        const { uri } = await Filesystem.getUri({
          path,
          directory: Directory.Cache,
        });
        const isAndroid = Capacitor.getPlatform() === 'android';
        if (isAndroid && uri.startsWith('content://')) {
          const bases = [
            'file:///data/data/com.striveai.fitness/cache',
            'file:///data/user/0/com.striveai.fitness/cache',
          ];
          const pathsToTry = bases.flatMap((b) => [`${b}/${path}`, `${b}/files/${path}`]);
          let shared = false;
          for (const fileUrl of pathsToTry) {
            try {
              await Share.share({
                title: t('components.shareWorkout.shareMetaTitle'),
                text: shareText,
                files: [fileUrl],
                dialogTitle: t('components.shareWorkout.shareMetaDialog'),
              });
              shared = true;
              break;
            } catch {
              // try next path
            }
          }
          if (!shared) throw new Error('Share failed');
        } else {
          await Share.share({
            title: t('components.shareWorkout.shareMetaTitle'),
            text: shareText,
            url: uri,
            dialogTitle: t('components.shareWorkout.shareMetaDialog'),
          });
        }
        toast.success(t('components.shareWorkoutExtra.toast.shared'));
      } else if (navigator.share && navigator.canShare) {
        const response = await fetch(generatedImage);
        const blob = await response.blob();
        const file = new File([blob], fileName, { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: t('components.shareWorkout.shareMetaTitle'),
            text: shareText,
            files: [file],
          });
          toast.success(t('components.shareWorkoutExtra.toast.shared'));
        } else {
          downloadFallback();
        }
      } else {
        downloadFallback();
      }
    } catch (error: any) {
      const isUserCancel = error?.name === 'AbortError' ||
        (typeof error?.message === 'string' && error.message.toLowerCase().includes('cancel'));
      if (!isUserCancel) {
        console.error('Error sharing:', error);
        toast.error(t('components.shareWorkoutExtra.toast.shareFailed'));
        if (Capacitor.isNativePlatform()) {
          downloadFallback();
        }
      }
    } finally {
      setIsSharing(false);
    }
  };

  const navigateTemplate = (direction: 'prev' | 'next') => {
    if (templates.length <= 1) return;
    const currentIndex = templates.indexOf(selectedTemplate);
    if (direction === 'prev') {
      const newIndex = currentIndex <= 0 ? templates.length - 1 : currentIndex - 1;
      setSelectedTemplate(templates[newIndex]);
    } else {
      const newIndex = currentIndex >= templates.length - 1 ? 0 : currentIndex + 1;
      setSelectedTemplate(templates[newIndex]);
    }
  };

  const renderTemplate = () => {
    if (!workout) return null;
    switch (selectedTemplate) {
      case 'classic':
        return <ShareTemplateClassic ref={cardRef} workout={workout} />;
      case 'minimal':
        return <ShareTemplateMinimal ref={cardRef} workout={workout} />;
      case 'bold':
        return <ShareTemplateBold ref={cardRef} workout={workout} />;
      case 'route':
        return <ShareTemplateRoute ref={cardRef} workout={workout} />;
      case 'splits':
        return <ShareTemplateSplits ref={cardRef} workout={workout} transparent={false} />;
      case 'splitsTransparent':
        return <ShareTemplateSplits ref={cardRef} workout={workout} transparent={true} />;
      case 'gymExercises':
        return <ShareTemplateGymExercises ref={cardRef} workout={workout} />;
      case 'gymSimple':
        return <ShareTemplateGymSimple ref={cardRef} workout={workout} />;
    }
  };

  if (!workout) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto z-[100000]"
        overlayClassName="z-[100000]"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShareIcon className="h-5 w-5 text-primary" />
            {t('components.shareWorkout.title')}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Hidden file input used as fallback for web photo picker */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFileInputChange}
          />

          {/* Template in portal to body - consistent position for html2canvas (fixes gym completion flow) */}
          {isOpen && workout && createPortal(
            <div
              aria-hidden
              style={{
                position: 'fixed',
                left: 0,
                top: 0,
                width: '1080px',
                minWidth: '1080px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                boxSizing: 'border-box',
                opacity: 0.01,
                pointerEvents: 'none',
                zIndex: -1,
                overflow: 'hidden',
              }}
            >
              {renderTemplate()}
            </div>,
            document.body
          )}

          {/* Template Selector with Navigation */}
          <div className="flex items-center justify-center gap-4 mb-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateTemplate('prev')}
              className="h-8 w-8 touch-manipulation min-w-[44px] min-h-[44px]"
              disabled={templates.length <= 1}
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="text-center min-w-[100px]">
              <p className="font-medium text-sm">{t(TEMPLATE_NAME_KEYS[selectedTemplate])}</p>
              <div className="flex justify-center gap-1.5 mt-1">
                {templates.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setSelectedTemplate(t)}
                    className={`w-2 h-2 rounded-full transition-all touch-manipulation min-w-[20px] min-h-[20px] ${
                      t === selectedTemplate
                        ? 'bg-primary scale-110'
                        : 'bg-muted-foreground/30'
                    }`}
                  />
                ))}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateTemplate('next')}
              className="h-8 w-8 touch-manipulation min-w-[44px] min-h-[44px]"
              disabled={templates.length <= 1}
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>

          {/* Generated Image Preview - centered for iOS */}
          <div className="flex justify-center items-center w-full">
            {selectedTemplate === 'route' && isCapturingLiveRoute && workout ? (
              // Native-screenshot path for the Route template: render the live
              // MapKit JS view directly inside the preview slot at a 9:16
              // share aspect. The native plugin snapshots only this rect, so
              // the user just sees a normal modal preview - no full-screen
              // flash.
              <div
                ref={liveRouteHostRef}
                style={{
                  position: 'relative',
                  height: 'min(60vh, 640px)',
                  aspectRatio: '9 / 16',
                  background: '#000',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
                }}
              >
                <LiveRouteCapture workout={workout} />
              </div>
            ) : isGenerating ? (
              <div className="w-full h-[380px] flex items-center justify-center bg-muted/20 rounded-xl">
                <div className="text-center space-y-2">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                  <p className="text-sm text-muted-foreground">{t('components.shareWorkout.generating')}</p>
                </div>
              </div>
            ) : generatedImage ? (
              <img
                src={generatedImage}
                alt={t('components.shareWorkout.previewAlt')}
                className="max-w-full h-auto rounded-xl shadow-lg block mx-auto"
                style={{ maxHeight: '380px', objectFit: 'contain' }}
              />
            ) : (
              <div className="w-full h-[380px] flex items-center justify-center bg-muted/20 rounded-xl">
                <p className="text-sm text-muted-foreground">{t('components.shareWorkout.previewLoading')}</p>
              </div>
            )}
          </div>

          {/* Photo overlay controls - only meaningful on transparent templates */}
          {allowPhotoOverlay && (
            <div className="rounded-xl border border-border/60 bg-muted/10 p-3 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">
                  {backgroundPhoto ? t('components.shareWorkout.photoApplied') : t('components.shareWorkout.photoUseAsBg')}
                </p>
                {backgroundPhoto && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setBackgroundPhoto(null)}
                    className="h-8 px-2 touch-manipulation"
                  >
                    <X className="h-4 w-4 mr-1" />
                    {t('components.shareWorkout.removePhoto')}
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 touch-manipulation"
                  onClick={() => handlePickPhoto('camera')}
                  disabled={isPickingPhoto || isGenerating}
                >
                  <CameraIcon className="h-4 w-4 mr-2" />
                  {t('components.shareWorkout.pickCamera')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 touch-manipulation"
                  onClick={() => handlePickPhoto('library')}
                  disabled={isPickingPhoto || isGenerating}
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  {t('components.shareWorkout.pickLibrary')}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                {t('components.shareWorkoutExtra.overlayHintLong')}
              </p>
            </div>
          )}

          {/* iOS: single "Save & Share" button; Android: "Share" + "Save to Photos"; Web: Share + Download */}
          <Button
            onClick={handleShare}
            disabled={!generatedImage || isSharing}
            className="h-14 w-full text-base bg-gradient-primary text-black font-semibold touch-manipulation"
          >
            <span className="flex flex-1 items-center justify-center gap-2">
              {isSharing ? (
                <Loader2 className="h-5 w-5 animate-spin shrink-0" />
              ) : (
                <ShareIcon className="h-5 w-5 shrink-0 text-primary" />
              )}
              {Capacitor.getPlatform() === 'ios' ? t('components.shareWorkout.saveAndShare') : t('components.shareWorkout.shareCta')}
            </span>
          </Button>

          {/* Android only: Save to Photos */}
          {generatedImage && Capacitor.getPlatform() === 'android' && (
            <Button
              variant="outline"
              onClick={handleSaveToPhotos}
              disabled={isSavingToPhotos}
              className="w-full touch-manipulation"
            >
              {isSavingToPhotos ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              {t('components.shareWorkout.saveToPhotos')}
            </Button>
          )}

          {/* Web only: Download fallback */}
          {generatedImage && !Capacitor.isNativePlatform() && (
            <Button
              variant="outline"
              onClick={downloadFallback}
              className="w-full touch-manipulation"
            >
              {t('components.shareWorkout.downloadCta')}
            </Button>
          )}

          <p className="text-xs text-center text-muted-foreground">
            {Capacitor.getPlatform() === 'ios'
              ? t('components.shareWorkoutExtra.footerIosShareSheet')
              : t('components.shareWorkoutExtra.footerWebShare')}
          </p>

          {saveSuccessToast && (
            <div
              className="fixed left-1/2 top-20 -translate-x-1/2 z-[2147483647] animate-in fade-in slide-in-from-top-2 duration-300 rounded-lg border bg-background px-4 py-3 shadow-lg"
              role="status"
              aria-live="polite"
            >
              <p className="text-sm font-medium text-foreground">{t('components.shareWorkout.savedBanner')}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
